import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Clock, MapPin, User } from "lucide-react";

const DAYS_EN = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAYS_MS = ["Isnin", "Selasa", "Rabu", "Khamis", "Jumaat", "Sabtu", "Ahad"];

function useDays() {
  const { lang } = useI18n();
  return lang === "ms" ? DAYS_MS : DAYS_EN;
}

function TimetableGrid({ classes }) {
  const days = useDays();
  const byDay = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    classes.forEach((c) => {
      if (c.day_of_week >= 0 && c.day_of_week < 7) map[c.day_of_week].push(c);
    });
    map.forEach((arr) => arr.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")));
    return map;
  }, [classes]);

  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {days.map((d, idx) => (
        <Card key={d} className="rounded-2xl" data-testid={`day-col-${idx}`}>
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-base font-bold">{d}</h3>
              <span className="num text-xs text-muted-foreground">{byDay[idx].length}</span>
            </div>
            {byDay[idx].length === 0 ? (
              <p className="text-xs text-muted-foreground">—</p>
            ) : (
              <ul className="space-y-2">
                {byDay[idx].map((c) => (
                  <li key={c.id} className="rounded-xl border border-border/60 bg-muted/30 p-3" data-testid={`class-${c.id}`}>
                    <div className="font-medium">{c.name}</div>
                    <div className="num text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {c.start_time} – {c.end_time}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{c.level}</span>
                      {c.room && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.room}</span>}
                      {c.teacher_name && <span className="flex items-center gap-1"><User className="h-3 w-3" /> {c.teacher_name}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function AdminTimetablePage() {
  const { t, lang } = useI18n();
  const days = useDays();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    level: "",
    day_of_week: "0",
    start_time: "16:00",
    end_time: "17:30",
    room: "",
    teacher_id: "",
    notes: "",
  });

  const load = () => api.get("/classes").then((r) => setClasses(r.data.classes));
  useEffect(() => {
    load();
    api.get("/users").then((r) => setTeachers(r.data.users.filter((u) => u.role === "teacher"))).catch(() => {});
  }, []);

  const save = async () => {
    try {
      await api.post("/classes", {
        ...form,
        day_of_week: Number(form.day_of_week),
        teacher_id: form.teacher_id || null,
      });
      toast.success(lang === "ms" ? "Kelas disimpan" : "Class saved");
      setOpen(false);
      setForm({ name: "", level: "", day_of_week: "0", start_time: "16:00", end_time: "17:30", room: "", teacher_id: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm(lang === "ms" ? "Padam kelas ini?" : "Delete this class?")) return;
    try {
      await api.delete(`/classes/${id}`);
      load();
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-timetable">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("timetable")}</h1>
          <p className="text-sm text-muted-foreground">
            {lang === "ms" ? "Terbitkan jadual kelas mingguan untuk ibu bapa lihat." : "Publish the weekly class schedule for parents to see."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full gap-2" data-testid="create-class-btn"><Plus className="h-4 w-4" /> {t("addClass")}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("addClass")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>{t("className")}</Label><Input data-testid="cls-name" className="h-11" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Math Advanced" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("level")}</Label><Input data-testid="cls-level" className="h-11" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="Primary 3" /></div>
                <div>
                  <Label>{t("day")}</Label>
                  <Select value={form.day_of_week} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                    <SelectTrigger data-testid="cls-day" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {days.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t("startTime")}</Label><Input data-testid="cls-start" className="h-11" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                <div><Label>{t("endTime")}</Label><Input data-testid="cls-end" className="h-11" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
              </div>
              <div><Label>{t("room")}</Label><Input data-testid="cls-room" className="h-11" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} placeholder="Room A / Online" /></div>
              <div>
                <Label>{t("teacher")}</Label>
                <Select value={form.teacher_id || "__none__"} onValueChange={(v) => setForm({ ...form, teacher_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="cls-teacher" className="h-11"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {teachers.map((tr) => <SelectItem key={tr.id} value={tr.id}>{tr.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={save} data-testid="cls-save" className="w-full rounded-full h-11">{t("submit")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <TimetableGrid classes={classes} />

      {classes.length > 0 && (
        <div className="rounded-2xl border border-border/70 p-4">
          <h2 className="mb-3 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {lang === "ms" ? "Padam kelas" : "Delete classes"}
          </h2>
          <div className="flex flex-wrap gap-2">
            {classes.map((c) => (
              <Button
                key={c.id}
                variant="outline"
                size="sm"
                onClick={() => del(c.id)}
                className="rounded-full"
                data-testid={`del-class-${c.id}`}
              >
                {c.name} · {days[c.day_of_week]} · {c.start_time} ×
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ParentTimetablePage() {
  const { t, lang } = useI18n();
  const [classes, setClasses] = useState([]);
  useEffect(() => { api.get("/classes").then((r) => setClasses(r.data.classes)); }, []);
  return (
    <div className="space-y-6" data-testid="parent-timetable">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("timetable")}</h1>
        <p className="text-sm text-muted-foreground">
          {lang === "ms" ? "Jadual kelas untuk anak-anak anda mengikut kelas mereka." : "Weekly schedule for your children based on their level."}
        </p>
      </div>
      {classes.length === 0 ? (
        <Card className="rounded-2xl"><CardContent className="p-6 text-sm text-muted-foreground">
          {lang === "ms"
            ? "Tiada kelas untuk kelas anak anda buat masa ini."
            : "No classes for your children's levels yet."}
        </CardContent></Card>
      ) : (
        <TimetableGrid classes={classes} />
      )}
    </div>
  );
}
